/**
 * QualificationStep.tsx — "Getting started", the one-screen qualification form
 * that used to be a dropdown at the bottom of Step 0 (CTO, 2026-09-12).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * ── WHAT MOVED, AND WHY IT MOVED HERE ───────────────────────────────────────
 * The CTO asked for the "what brings you here today" dropdown to come OFF the
 * first capture form and become a short flow before the questionnaire. Taking
 * it off the form is a straight win: Step 0 is the highest-drop-off screen in
 * the journey and it was carrying a question that earns the business a segment
 * and earns the patient nothing.
 *
 * WHERE it went is the compliance decision. This screen sits AFTER the
 * projection interstitial and BEFORE the questionnaire, which means every lane
 * that reaches the questionnaire passes through it — the in-portal capture lane
 * AND the Webflow widget hand-off, which is where most real leads come from and
 * which never renders Step 0 at all. Putting it between Step 0 and the
 * projection would have missed that lane entirely.
 *
 * ⚠️ IT IS STILL A PRE-ELIGIBILITY PUBLIC SURFACE. Nothing about this position
 * relaxes R28 or UK GDPR Art 9: no eligibility decision has been made about the
 * visitor yet, and there is no compliance guard in front of this component. The
 * only question it may ask is a commercial one, and the vocabulary boundary is
 * argued in full in `leadReadiness.ts` — read that before adding anything here.
 * `qualificationFlow.spec.tsx` fails the build if a health-shaped question
 * appears on this screen.
 *
 * ── DESIGN ──────────────────────────────────────────────────────────────────
 * Option buttons rather than the old native <select>: on its own screen there
 * is room for them, they are one tap instead of three, and the whole answer set
 * is visible without opening a picker — so a patient can see that none of the
 * four asks anything about their health before they choose.
 *
 * ── REQUIRED, AND THE TAP IS THE ANSWER (CTO, 2026-09-25) ───────────────────
 * *"They shouldn't be able to skip this question, so as soon as they select an
 * answer for one of these things, it should take them to the next page."*
 *
 * This REVERSES the 2026-09-12 design, which made the answer optional, put a
 * Continue under the options, told the patient "You can skip it", let a second
 * tap clear the answer, and argued at length here for NOT auto-advancing. All
 * of that is gone:
 *
 *   - NO CONTINUE AND NO SKIP. The screen has no action row, so the only ways
 *     off it are an answer or Back. Nothing on it calls the question optional
 *     (there has been no help line under it since 2026-09-26, "unneeded text").
 *   - ONE TAP ANSWERS AND ADVANCES, exactly like the questionnaire's
 *     single-selects (`SingleSelectQuestion` in QuestionCard.tsx): the option
 *     is marked picked and the answer handed up in the SAME handler. There is
 *     no timing constant to share with the questionnaire because the
 *     questionnaire has none — its only "beat" is the answer save's round
 *     trip, and this screen has no save to wait on (the readiness record is
 *     fire-and-forget by design; see JourneyPage `continueFromQualification`).
 *     Inventing a delay here would be a second timing rule with no owner.
 *   - `onDone` FIRES AT MOST ONCE PER MOUNT. A double tap, a tap on a second
 *     option, or a repeat Enter that lands before React has swapped the screen
 *     would otherwise hand up a second answer — and the LAST one would win the
 *     remembered answer while the first had already been recorded. A ref, not
 *     state, because the second tap can arrive before any re-render. The caller
 *     always moves the journey on in `onDone` (JourneyPage does, synchronously),
 *     so a spent guard can never strand a patient here; a fresh visit is a
 *     fresh mount with a fresh guard.
 *   - A SECOND TAP NO LONGER CLEARS. Clearing only existed so an OPTIONAL answer
 *     could be withdrawn; on a required one it would be a tap that does
 *     nothing visible.
 *   - COMING BACK: `initialReadiness` still pre-selects the previous answer, and
 *     tapping ANY option — the pre-selected one included — answers and
 *     advances. There is no Continue to confirm an unchanged answer with.
 *   - KEYBOARD: every option is a native `<button>` (kit `OptionButton`), so
 *     Enter and Space activate it through the same click handler and the same
 *     once-only guard. No key handler of our own — one would fire alongside the
 *     browser's activation click.
 *
 * WHAT DID NOT CHANGE: the journey still records the answer against the lead
 * with the same fire-and-forget POST, once per lead, and fires no dataLayer
 * event from this screen (it never did). The server's readiness field stays
 * OPTIONAL: lanes that never show this screen — the clinician plan link and a
 * cold `?assessment=` restore — carry no answer; a Webflow embed's capture POST
 * may or may not carry `intent` (the widget HAND-OFF itself still passes
 * through this screen and must answer it, as the top of this header says); and
 * a patient can still leave with Back or by closing the tab. Only THIS screen
 * stopped offering a skip.
 */
import { useRef, useState } from 'react';
import { Eyebrow, OptionButton, Title } from '../ui/kit';
import { JourneyScreen } from './JourneyScreen';
import { READINESS_OPTIONS, READINESS_QUESTION, type LeadReadinessKey } from './leadReadiness';

export function QualificationStep({
  percent,
  initialReadiness,
  onBack,
  onDone,
}: {
  percent?: number;
  /**
   * Pre-selects the patient's previous answer when they come BACK to this
   * screen (questionnaire → Back → projection → forward again). Re-asking a
   * question somebody already answered, with their answer thrown away, reads as
   * the journey having lost their place. `null`/absent = no answer yet, and
   * nothing is pre-selected: a default would manufacture a segment out of
   * everyone who did not read the question.
   */
  initialReadiness?: LeadReadinessKey | null;
  onBack: () => void;
  /**
   * The patient's answer — always a real key, never `null`: the question is
   * required (CTO, 2026-09-25). Called at most once per mount; the caller must
   * move the journey on (see the header).
   */
  onDone: (readiness: LeadReadinessKey) => void;
}) {
  const [picked, setPicked] = useState<LeadReadinessKey | null>(initialReadiness ?? null);
  const answeredRef = useRef(false);

  function answer(key: LeadReadinessKey) {
    if (answeredRef.current) return;
    answeredRef.current = true;
    setPicked(key);
    onDone(key);
  }

  return (
    <JourneyScreen onBack={onBack} percent={percent}>
      <Eyebrow>Getting started</Eyebrow>
      <Title>{READINESS_QUESTION}</Title>
      <div style={{ marginTop: 'var(--gps-s-4)' }}>
        {READINESS_OPTIONS.map((option) => (
          <OptionButton
            key={option.key}
            control="radio"
            selected={picked === option.key}
            onClick={() => answer(option.key)}
          >
            {option.label}
          </OptionButton>
        ))}
      </div>
    </JourneyScreen>
  );
}
